import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Tooltip,
} from "../../../../components";
import { BalancaService, notification } from "../../../../services";

const MARCAS = ["Toledo", "ToledoPrt2"];
const BAUD_RATES = [2400, 4800, 9600, 19200, 38400];

export default function ConfiguracoesBalancaDialog({ open, setOpen }) {
  const balancaService = new BalancaService();
  const [config, setConfig] = useState({});
  const [portas, setPortas] = useState([]);
  const [testando, setTestando] = useState(false);
  const [resultadoTeste, setResultadoTeste] = useState(null);

  useEffect(() => {
    if (open) {
      carregar();
    }
  }, [open]);

  const carregar = async () => {
    setResultadoTeste(null);
    const [resConfig, resPortas] = await Promise.all([
      balancaService.getConfig(),
      balancaService.listarPortas(),
    ]);
    if (!resConfig.isAxiosError && resConfig.data) {
      setConfig(resConfig.data);
    }
    if (!resPortas.isAxiosError && Array.isArray(resPortas.data)) {
      setPortas(resPortas.data);
    }
  };

  const onChangeTexto = (event) => {
    const { name, value } = event.target;
    setConfig((prev) => ({ ...prev, [name]: value }));
  };

  const onChangeNumero = (event) => {
    const { name, value } = event.target;
    setConfig((prev) => ({
      ...prev,
      [name]: value === "" ? "" : Number(value),
    }));
  };

  const onChangeSwitch = (event) => {
    const { name, checked } = event.target;
    setConfig((prev) => ({ ...prev, [name]: checked }));
  };

  const detectarPortas = async () => {
    const res = await balancaService.listarPortas();
    if (!res.isAxiosError && Array.isArray(res.data)) {
      setPortas(res.data);
      notification.sucessoGenericos(
        `${res.data.length} porta(s) encontrada(s).`,
      );
    } else {
      notification.alertaGenericos("Não foi possível listar as portas.");
    }
  };

  const testar = async () => {
    setResultadoTeste(null);
    setTestando(true);
    await balancaService.salvarConfig(config);
    const res = await balancaService.lerPeso();
    setTestando(false);
    if (res.isAxiosError || !res.data) {
      setResultadoTeste({ ok: false, texto: "Sem comunicação com a balança." });
      return;
    }
    const { peso, unidade, estavel, status } = res.data;
    if (status === "PratoVazio") {
      setResultadoTeste({
        ok: false,
        texto: "Prato vazio. Coloque um peso e teste novamente.",
      });
    } else if (estavel) {
      setResultadoTeste({ ok: true, texto: `Peso lido: ${peso} ${unidade}` });
    } else {
      setResultadoTeste({ ok: false, texto: `Peso instável (${status}).` });
    }
  };

  const salvar = async () => {
    const res = await balancaService.salvarConfig(config);
    if (res.isAxiosError) {
      notification.alertaGenericos(
        res?.response?.data?.message ||
          "Não foi possível salvar a configuração.",
      );
      return;
    }
    notification.sucessoGenericos("Configuração da balança salva.");
    setOpen(false);
  };

  const opcoesPortas = [...new Set([config.porta, ...portas].filter(Boolean))];
  const opcoesBaud = [
    ...new Set([config.baudRate, ...BAUD_RATES].filter(Boolean)),
  ].sort((a, b) => a - b);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      PaperComponent="div"
      open={open}
      aria-labelledby="form-dialog-title"
      onClose={() => setOpen(false)}
    >
      <DialogContent>
        <Card className="p-3">
          <CardHeader title="Configuração da balança" />
          <CardContent className="p-3">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Select
                  fullWidth
                  label="Marca / protocolo"
                  name="marca"
                  value={config.marca ?? ""}
                  onChange={onChangeTexto}
                >
                  {MARCAS.map((marca) => (
                    <MenuItem key={marca} value={marca}>
                      {marca}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={8} sm={4}>
                <Select
                  fullWidth
                  label="Porta"
                  name="porta"
                  value={config.porta ?? ""}
                  onChange={onChangeTexto}
                >
                  {opcoesPortas.map((porta) => (
                    <MenuItem key={porta} value={porta}>
                      {porta}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={4} sm={2} className="d-flex align-items-center">
                <Tooltip title="Detectar Portas COM" arrow placement="bottom">
                  <Button
                    id="buttonDetectarPortas"
                    variant="contained"
                    size="small"
                    sx={{
                      height: "35px",
                      borderRadius: "20%",
                      minWidth: "15px",
                      boxShadow: "none",
                    }}
                    onClick={detectarPortas}
                  >
                    <i
                      className="ph-fill ph-binoculars"
                      style={{ fontSize: 15, color: "white" }}
                    ></i>
                  </Button>
                </Tooltip>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Select
                  fullWidth
                  label="Velocidade (baud)"
                  name="baudRate"
                  value={config.baudRate ?? ""}
                  onChange={onChangeNumero}
                >
                  {opcoesBaud.map((baud) => (
                    <MenuItem key={baud} value={baud}>
                      {baud}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={6} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Casas decimais"
                  name="casasDecimais"
                  value={config.casasDecimais ?? ""}
                  onChange={onChangeNumero}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" className="text-muted">
                  Ajustes avançados (normalmente não precisa mexer)
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Timeout (ms)"
                  name="timeoutMs"
                  value={config.timeoutMs ?? ""}
                  onChange={onChangeNumero}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Estabilidade (ms)"
                  name="estabilidadeTimeoutMs"
                  value={config.estabilidadeTimeoutMs ?? ""}
                  onChange={onChangeNumero}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Intervalo (ms)"
                  name="intervaloEstabilidadeMs"
                  value={config.intervaloEstabilidadeMs ?? ""}
                  onChange={onChangeNumero}
                  variant="outlined"
                />
              </Grid>
              {resultadoTeste && (
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    className={
                      resultadoTeste.ok ? "text-success" : "text-danger"
                    }
                  >
                    {resultadoTeste.texto}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
          <CardActions>
            <Button variant="contained" disableElevation onClick={salvar}>
              <i
                className="ph-fill ph-check-circle"
                style={{ color: "white", fontSize: 22, marginRight: 10 }}
              />
              Salvar
            </Button>
            <Button
              color="padrao"
              variant="contained"
              disableElevation
              disabled={testando}
              onClick={testar}
            >
              <i
                className="ph-fill ph-scales"
                style={{ fontSize: 22, marginRight: 10 }}
              />
              {testando ? "Testando..." : "Testar"}
            </Button>
            <Button
              color="inherit"
              variant="contained"
              disableElevation
              onClick={() => setOpen(false)}
            >
              <i
                className="ph-fill ph-x-circle"
                style={{ fontSize: 22, marginRight: 10 }}
              />
              Fechar
            </Button>
          </CardActions>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
